import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNav, type PageId } from "../context/NavContext";
import { useAuth } from "../context/AuthContext";
import { Button, Logo } from "./ui";
import { Ic } from "./icons";
import { SignInDialog } from "./SignInDialog";
import { signOut } from "../lib/auth";

/* Header partagé entre les pages publiques (Landing, Brand, Pricing).
 * Fait deux choses :
 *  - Une nav cohérente sur les 3 pages (Produit, Tarifs, Notre histoire)
 *    avec mise en évidence de la page active.
 *  - Une zone d'actions auth-aware :
 *      • non-loggé → Se connecter + Essayer gratuitement (ouvrent SignInDialog)
 *      • loggé    → CTA "Nouvelle analyse" + avatar avec dropdown menu
 *                   (Dashboard / Mes fiches / Paramètres / Déconnexion).
 *
 *  Avant cette unification chaque page définissait son propre header :
 *  navigation incohérente, CTA "Essayer gratuitement" affiché même quand
 *  l'utilisateur était déjà loggé, aucun indicateur visuel d'identité, et
 *  aucun moyen de se déconnecter sans repasser par la sidebar de l'app.
 */

type NavItem = { label: string; page: PageId };

const NAV: NavItem[] = [
  { label: "Produit", page: "landing" },
  { label: "Tarifs", page: "pricing" },
  { label: "Notre histoire", page: "brand" },
];

export function PublicHeader({ active }: { active: PageId }) {
  const { go } = useNav();
  const { user } = useAuth();
  const [signInOpen, setSignInOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Ferme le menu au clic extérieur ou à Échap.
  useEffect(() => {
    if (!menuOpen) return;
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [menuOpen]);

  const initials = user?.displayName
    ? user.displayName
        .split(" ")
        .map((s) => s[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : user?.email?.slice(0, 2).toUpperCase() ?? "??";

  const displayName = user?.displayName ?? user?.email ?? "Mon compte";

  async function handleSignOut() {
    setMenuOpen(false);
    try {
      await signOut();
      go("landing");
    } catch (err) {
      console.error("signOut failed", err);
    }
  }

  function nav(page: PageId) {
    setMenuOpen(false);
    go(page);
  }

  return (
    <>
      <header className="sticky top-0 z-30 backdrop-blur bg-canvas/85 border-b border-ink-200/40">
        <div className="max-w-[1240px] mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-3 sm:gap-4">
          <button
            onClick={() => go("landing")}
            aria-label="Retour à l'accueil"
            className="cursor-pointer shrink-0"
          >
            <Logo />
          </button>

          <nav className="hidden md:flex items-center gap-1 text-[13px]">
            {NAV.map((item) => {
              const isActive = active === item.page;
              return (
                <button
                  key={item.page}
                  onClick={() => go(item.page)}
                  aria-current={isActive ? "page" : undefined}
                  className={`px-3 h-9 rounded-full transition ${
                    isActive
                      ? "text-ink-900 font-medium bg-ink-100"
                      : "text-ink-700 hover:text-ink-900 hover:bg-ink-100/60"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            {user ? (
              <>
                <Button
                  variant="dark"
                  size="sm"
                  iconRight={<Ic.Arrow size={14} />}
                  onClick={() => go("dashboard")}
                  className="hidden sm:inline-flex"
                >
                  Accéder à mon compte
                </Button>
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => setMenuOpen((v) => !v)}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    aria-label="Menu utilisateur"
                    title={displayName}
                    className="flex items-center gap-1.5 pl-0.5 pr-2 h-9 rounded-full bg-paper hairline transition hover:bg-ink-50"
                  >
                    {user.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt=""
                        referrerPolicy="no-referrer"
                        className="w-7 h-7 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-ink-900 text-lime-300 grid place-items-center text-[11px] font-semibold">
                        {initials}
                      </div>
                    )}
                    <Ic.ChevronDown size={12} className="text-ink-500" />
                  </button>
                  {menuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 mt-2 w-[260px] bg-paper hairline rounded-xl2 shadow-pop overflow-hidden z-40"
                    >
                      <div className="px-4 py-3 border-b border-ink-200/70 flex items-center gap-3">
                        {user.photoURL ? (
                          <img
                            src={user.photoURL}
                            alt=""
                            referrerPolicy="no-referrer"
                            className="w-9 h-9 rounded-full object-cover shrink-0"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-ink-900 text-lime-300 grid place-items-center text-[12px] font-semibold shrink-0">
                            {initials}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium text-ink-900 truncate">
                            {user.displayName ?? "Mon compte"}
                          </div>
                          {user.email && (
                            <div className="text-[11.5px] text-ink-500 truncate">
                              {user.email}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="py-1">
                        <MenuItem icon={<Ic.Grid size={14} />} onClick={() => nav("dashboard")}>
                          Dashboard
                        </MenuItem>
                        <MenuItem icon={<Ic.File size={14} />} onClick={() => nav("fiches")}>
                          Mes fiches
                        </MenuItem>
                        <MenuItem
                          icon={<Ic.Plus size={14} />}
                          onClick={() => nav("nouvelle")}
                          className="sm:hidden"
                        >
                          Nouvelle analyse
                        </MenuItem>
                        <MenuItem icon={<Ic.Settings size={14} />} onClick={() => nav("settings")}>
                          Paramètres
                        </MenuItem>
                      </div>
                      <div className="border-t border-ink-200/70 py-1">
                        <MenuItem
                          icon={<LogOutIcon />}
                          onClick={handleSignOut}
                          tone="danger"
                        >
                          Se déconnecter
                        </MenuItem>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSignInOpen(true)}
                  className="hidden sm:inline-flex"
                >
                  Se connecter
                </Button>
                <Button
                  variant="dark"
                  size="sm"
                  iconRight={<Ic.Arrow size={14} />}
                  onClick={() => setSignInOpen(true)}
                >
                  Essayer gratuitement
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <SignInDialog
        open={signInOpen}
        onClose={() => setSignInOpen(false)}
        onSuccess={() => go("dashboard")}
      />
    </>
  );
}

function MenuItem({
  icon,
  children,
  onClick,
  className = "",
  tone,
}: {
  icon: ReactNode;
  children: ReactNode;
  onClick: () => void;
  className?: string;
  tone?: "danger";
}) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      className={`w-full px-4 py-2 flex items-center gap-2.5 text-[13px] text-left transition ${
        tone === "danger"
          ? "text-rose-700 hover:bg-rose-50"
          : "text-ink-800 hover:bg-ink-50"
      } ${className}`}
    >
      <span className={tone === "danger" ? "text-rose-500" : "text-ink-500"}>
        {icon}
      </span>
      {children}
    </button>
  );
}

function LogOutIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}
